import { type Icon as IconType, Sparkle } from "@phosphor-icons/react";

import IconAvatar from "common/components/IconAvatar";
import RenderIf from "common/components/RenderIf";
import Skeleton from "common/components/ui/Skeleton";
import { Tag } from "common/components/ui/Tag";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import type { ReactNode } from "react";

interface ReviewItemProps {
  title: string;
  value: ReactNode;
  icon: IconType;
  isAiEnabled?: boolean;
  isLoading?: boolean;
}

export default function ReviewItem({
  title,
  value,
  icon: Icon,
  isAiEnabled = false,
  isLoading = false,
}: ReviewItemProps) {
  const isDesktop = useTwBreakpoint("lg");

  function getContent() {
    if (isDesktop) {
      return (
        <>
          <IconAvatar>
            <Icon />
          </IconAvatar>

          <span className="text-sm-bold">{title}</span>

          <RenderIf condition={isAiEnabled}>
            <Tag leftIcon={<Sparkle />} variant="gradient">
              AI
            </Tag>
          </RenderIf>

          {isLoading ? (
            <Skeleton className="ml-auto h-[19px] w-40" />
          ) : (
            <span className="ml-auto text-sm-bold">{value}</span>
          )}
        </>
      );
    }

    return (
      <>
        <IconAvatar size="sm">
          <Icon />
        </IconAvatar>

        <div className="flex flex-col gap-y-0.5">
          <div className="flex items-center gap-1">
            <span className="line-clamp-1 text-sm-bold">{title}</span>

            <RenderIf condition={isAiEnabled}>
              <Tag variant="gradient" className="p-0">
                <Sparkle />
              </Tag>
            </RenderIf>
          </div>

          {isLoading ? (
            <Skeleton className="h-[19px] w-40" />
          ) : (
            <span className="text-xs-medium text-black-500">{value}</span>
          )}
        </div>
      </>
    );
  }

  return (
    <div className="flex items-center gap-x-2 border-b border-black-200 py-3 first:pt-0 last:border-b-0 last:pb-0 lg:gap-x-2.5">
      {getContent()}
    </div>
  );
}
